"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const stepsData = [{
  number: "01",
  title: "Technical & Content Audit",
  description: "I personally analyze your website structure, crawlability, and search visibility to pinpoint hidden errors and untapped growth opportunities.",
  badgeText: "Audit",
  feature1: "In-depth Site Health Crawl",
  feature2: "Keyword & Intent Analysis",
  feature3: "Competitor Gap Benchmark"
}, {
  number: "02",
  title: "Strategy & On-Page Optimization",
  description: "I map target queries to dedicated pages, refine meta tags, restructure headings, and tune content architecture for search engines and users.",
  badgeText: "Optimization",
  feature1: "Precision Keyword Mapping",
  feature2: "Internal Link Restructuring",
  feature3: "User Experience Tuning"
}, {
  number: "03",
  title: "Citations & Authority Building",
  description: "I build robust digital footprints and clean directory citations to establish authentic domain trust and power local or national rankings.",
  badgeText: "Citations",
  feature1: "Local Citation Audit & Cleanup",
  feature2: "Entity Alignment",
  feature3: "High-Quality Brand Mentions"
}, {
  number: "04",
  title: "Reviews & Reputation Strategy",
  description: "I implement systematic workflows to capture high-value customer reviews and optimize your Google Business Profile for trust signals.",
  badgeText: "Reviews",
  feature1: "Google Business Optimization",
  feature2: "Review Capture Workflows",
  feature3: "Local Pack Visibility Boost"
}, {
  number: "05",
  title: "Schema Markup & AI Search",
  description: "I author bespoke JSON-LD structured data and optimize content for modern AI-powered search engines and semantic search crawlers.",
  badgeText: "Schema",
  feature1: "Structured JSON-LD Integration",
  feature2: "Rich Snippet Enablement",
  feature3: "Generative AI Search Readiness"
}, {
  number: "06",
  title: "Transparent Reporting & Growth",
  description: "I deliver clear, outcome-focused monthly reports tracking genuine rankings, qualified organic leads, and actionable next steps.",
  badgeText: "Reporting",
  feature1: "Custom Live Dashboards",
  feature2: "Direct 1-on-1 Monthly Reviews",
  feature3: "Continuous Search Growth"
}];
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="methodologystepprocess" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-accent/10 blur-3xl pointer-events-none rounded-full -z-10" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-primary/5 blur-3xl pointer-events-none rounded-full -z-10" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-accent/30">
            Methodology & Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground mb-6">
            A Hands-On SEO Process Designed for Sustainable Growth
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            You work directly with me—never account managers or junior staff. Here is the exact 6-step framework I follow to deliver verified, long-term search performance.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="hidden lg:block absolute top-12 left-12 right-12 h-0.5 bg-gradient-to-r from-primary via-accent to-primary/40 -z-10 opacity-30" />

          {stepsData.map((step, i) => <motion.div key={step.number} data-index={i} variants={itemVariants} className="h-full">
              <Card className="relative h-full border border-border bg-card shadow-sm hover:shadow-md hover:border-accent/50 transition-[border-color,box-shadow] duration-300 flex flex-col justify-between group overflow-hidden">
                <div className="p-6 pb-2">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground font-bold font-serif text-lg flex items-center justify-center shadow-md shadow-primary/20 group-hover:scale-105 transition-transform duration-300">
                      {step.number}
                    </div>
                    <Badge variant="outline" className="border-accent/40 bg-accent/10 text-foreground text-xs font-medium px-2.5 py-0.5">
                      {step.badgeText}
                    </Badge>
                  </div>

                  <CardTitle className="text-xl font-bold font-serif text-card-foreground mb-3 group-hover:text-primary transition-colors">
                    {step.title}
                  </CardTitle>

                  <CardDescription className="text-muted-foreground text-sm leading-relaxed mb-6">
                    {step.description}
                  </CardDescription>
                </div>

                <CardContent className="pt-0 pb-6 px-6 mt-auto">
                  <div className="border-t border-border pt-4 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                      <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                      <span>{step.feature1}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                      <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                      <span>{step.feature2}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                      <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                      <span>{step.feature3}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="max-w-4xl mx-auto rounded-2xl bg-secondary/60 border border-accent/30 p-8 md:p-10 text-center shadow-sm">
          <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground mb-4">
            Ready to improve your organic rankings with a dedicated SEO consultant?
          </h3>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto mb-8">
            I limit my client intake to ensure every business receives my complete personal attention, customized strategy, and direct consultation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md gap-2" asChild>
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto border-border bg-background hover:bg-accent/10 hover:text-foreground" asChild>
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}